import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import { authAPI } from '../api';

// 사용자 타입 정의
export interface User {
  id: number;
  username: string;
  email?: string;
  role?: string;
  roleId?: number;
  points?: number;
  nickname?: string;
  createdAt?: string;
  updatedAt?: string;
}

// API 응답 타입 정의
interface AuthResponse {
  data:{
    user?: User;
    token?: string;
    message?: string;
    isLoggedIn?: boolean;
    errors?: { msg: string }[];
  }
}

// 인증 컨텍스트 타입 정의
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUserInfo: ()=>void;
}

// 인증 컨텍스트 생성
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 컨텍스트 제공자 props 타입
interface AuthProviderProps {
  children: ReactNode;
}

// 인증 컨텍스트 제공자 컴포넌트
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState<boolean>(false);

  const updateUserInfo = useCallback(async () => {
    try {
      // 로컬 스토리지에서 토큰 확인
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        setAuthChecked(true);
        return;
      }
      
      const response = await authAPI.checkAuth();
      const data = response.data as unknown as AuthResponse;
      
      if (data && data.data.isLoggedIn && data.data.user) {
        setIsAuthenticated(true);
        setUser(data.data.user);
      } else {
        // 인증되지 않은 경우 명시적으로 상태 초기화
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('token'); // 토큰 제거
      }
    } catch (err) {
      console.error('인증 상태 확인 오류:', err);
      // 오류 발생 시 인증되지 않은 것으로 처리 
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('token'); // 오류 발생 시 토큰 제거
    } finally {
      // 인증 확인 작업이 완료됨을 표시
      setAuthChecked(true);
    }
  }, []);

  // 초기 로그인 상태 확인
  useEffect(() => {
    // 이미 인증 확인을 했으면 다시 하지 않음
    if (authChecked) return;

    async function checkAuthStatus(){
      setLoading(true);
      await updateUserInfo();
      setLoading(false);
    }
    checkAuthStatus();
  }, [authChecked, updateUserInfo]);

  // 로그인 함수
  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authAPI.login(username, password);
      const data = response.data as unknown as AuthResponse;
      
      if (data && data.data.user) {
        setIsAuthenticated(true);
        setUser(data.data.user);
        
        // 토큰이 있다면 로컬 스토리지에 저장
        if (data.data.token) {
          localStorage.setItem('token', data.data.token);
        }
        
        // 로그인 성공 시 인증 확인 상태 업데이트
        setAuthChecked(true);
      } else {
        throw new Error('로그인에 실패했습니다. 응답에 사용자 정보가 없습니다.');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '로그인 중 오류가 발생했습니다.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  },[]);

  // 회원가입 함수
  const register = useCallback(async (userData: any): Promise<AuthResponse> => {
    try {
      setLoading(true);
      setError(null);
      
      const resp = await authAPI.register(userData);
      const response = resp.data;
      // 회원가입 후 자동 로그인 여부는 서버 구현에 따라 다름
      if (response && response.data.user) {
        setIsAuthenticated(true);
        setUser(response.data.user);
        
        // 토큰이 있다면 로컬 스토리지에 저장
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
      }

      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.errors[0]?.msg || '회원가입 중 오류가 발생했습니다.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  },[]);

  // 로그아웃 함수
  const logout = useCallback(async () => {
    setLoading(true);
    
    try {
      await authAPI.logout();
      setIsAuthenticated(false);
      setUser(null);
      
      // 로컬 스토리지에서 토큰 제거
      localStorage.removeItem('token');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '로그아웃 중 오류가 발생했습니다.';
      setError(errorMessage);
      // 로그아웃 실패해도 클라이언트에서는 로그아웃 처리
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  // 에러 상태 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 컨텍스트 값
  const contextValue: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
    updateUserInfo
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// 인증 컨텍스트 사용을 위한 커스텀 훅
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth는 AuthProvider 내부에서만 사용할 수 있습니다.');
  }
  return context;
}; 